import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { mapLanguageToComponentProps } from 'interview/i18n'
import styled from 'styled-components'
import { LoadingIndicator } from 'components/styled/loading-indicator'
import { Body, Subtitle } from 'components/styled/typography'
import { Flex } from 'components/styled/containers/flex'
import { Icon } from 'components/styled/icon'

const propTypes = {
  i18n: PropTypes.shape({
    sequencedInterview: PropTypes.shape({}),
  }).isRequired,
  qualityTest: PropTypes.shape({
    audio: PropTypes.shape({}),
    video: PropTypes.shape({}),
    testing: PropTypes.bool,
    results: PropTypes.shape({}),
  }).isRequired,
  networkTest: PropTypes.shape({
    success: PropTypes.bool,
    testing: PropTypes.bool,
    results: PropTypes.shape({}),
    failedTests: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  errorType: PropTypes.string.isRequired,
  reconnecting: PropTypes.bool.isRequired,
  skipQualityTest: PropTypes.bool.isRequired,
}

const mapStateToProps = state => ({
  qualityTest: state.interview.sequencedInterview.qualityTest,
  networkTest: state.interview.sequencedInterview.networkTest,
  errorType: state.interview.sequencedInterview.errorType,
  reconnecting: state.global.opentok.reconnecting,
})

const StyledTestResultWrapper = styled.div`
  height: fit-content;
  padding: ${({ theme }) => theme.space.s16};
  align-items: center;
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.space.s4};
  background-color: ${({ theme }) => theme.color.grey05};
  width: ${({ theme }) => theme.space.s240};
  height: ${({ theme }) => theme.space.s128};

  ${({ theme }) => theme.mediaQuery.tablet} {
    height: 100%;
    width: 100%;
  }
`

const ResultsGroup = styled(Flex)`
  column-gap: ${({ theme }) => theme.space.s20};
  margin-bottom: ${({ theme }) => theme.space.s20};
  flex-direction: row;

  ${({ theme }) => theme.mediaQuery.tablet} {
    flex-direction: column;
    width: 100%;
    row-gap: ${({ theme }) => theme.space.s20};
  }
`

export class NetworkTestResults extends Component {
  constructor(props) {
    super(props)

    this.resultContainerRef = React.createRef()
  }

  componentDidUpdate(prevProps) {
    const { qualityTest } = this.props

    if (
      prevProps.qualityTest.results !== qualityTest.results &&
      this.resultContainerRef &&
      this.resultContainerRef.current
    ) {
      this.resultContainerRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  mosScoreRating(context, mos) {
    const {
      i18n,
      qualityTest: { results },
    } = this.props

    // Mos score does not really seem to correlate with the bitrate value so returning bad quality text if audio results are not supported
    // it shows very high mos scores for audio even when the test itself returns supported false with "Bandwidth too low" reason
    if (context === 'audio' && !results.audio.supported) {
      return (
        <Body color="error110">
          {i18n.sequencedInterview.networkTest.qualityRating.bad}
        </Body>
      )
    }
    if (mos >= 3.8) {
      return (
        <Body color="success110">
          {i18n.sequencedInterview.networkTest.qualityRating.excellent}
        </Body>
      )
    }
    if (mos >= 3.1) {
      return (
        <Body color="success110">
          {i18n.sequencedInterview.networkTest.qualityRating.good}
        </Body>
      )
    }
    if (mos >= 2.4) {
      return (
        <Body color="warn110">
          {i18n.sequencedInterview.networkTest.qualityRating.fair}
        </Body>
      )
    }
    if (mos >= 1.7) {
      return (
        <Body color="error110">
          {i18n.sequencedInterview.networkTest.qualityRating.poor}
        </Body>
      )
    }
    return (
      <Body color="error110">
        {i18n.sequencedInterview.networkTest.qualityRating.bad}
      </Body>
    )
  }

  supportedText(media) {
    const { i18n } = this.props
    if (media.supported) {
      return (
        <Body color="success110">
          {i18n.sequencedInterview.networkTest.yes}
        </Body>
      )
    }
    return (
      <Body color="error110">{i18n.sequencedInterview.networkTest.no}</Body>
    )
  }

  renderIcon(context) {
    const { networkTest, qualityTest, i18n } = this.props
    const textKeys = i18n.sequencedInterview.networkTest

    const success =
      context === 'network'
        ? networkTest.results.success
        : qualityTest.testSuccess
    const testPassed =
      context === 'network'
        ? textKeys.testPassedNetwork
        : textKeys.testPassedQuality
    const testFailed =
      context === 'network'
        ? textKeys.testFailedNetwork
        : textKeys.testFailedQuality

    if (success) {
      return (
        <>
          <Icon type="check_circle_outline" size="s40" color="success110" />
          <Body color="success110">{testPassed}</Body>
        </>
      )
    }
    return (
      <>
        <Icon type="highlight_off" size="s40" color="error110" />
        <Body color="error110">{testFailed}</Body>
      </>
    )
  }

  renderNetWorkTestResults() {
    const { networkTest, i18n, errorType, reconnecting } = this.props

    return (
      <StyledTestResultWrapper>
        <Subtitle>
          {i18n.sequencedInterview.networkTest.networkTestResults}
        </Subtitle>
        {networkTest.testing && !errorType && !reconnecting && (
          <>
            <LoadingIndicator />
            <Body>{i18n.sequencedInterview.networkTest.inProgress}</Body>
          </>
        )}
        {networkTest.results && this.renderIcon('network')}
      </StyledTestResultWrapper>
    )
  }

  renderQualityTestResults() {
    const {
      qualityTest,
      networkTest,
      i18n,
      errorType,
      reconnecting,
      skipQualityTest,
    } = this.props
    if (!skipQualityTest) {
      return (
        <StyledTestResultWrapper>
          <Subtitle>
            {i18n.sequencedInterview.networkTest.qualityTestResults}
          </Subtitle>
          {(qualityTest.testing || networkTest.testing) &&
            !errorType &&
            !reconnecting && (
              <>
                <LoadingIndicator />
                <Body>{i18n.sequencedInterview.networkTest.inProgress}</Body>
              </>
            )}
          {qualityTest.results &&
            !networkTest.testing &&
            this.renderIcon('quality')}
        </StyledTestResultWrapper>
      )
    }

    return null
  }

  renderQualityStats() {
    const {
      i18n,
      qualityTest: { results },
    } = this.props

    return (
      <>
        <div ref={this.resultContainerRef} />
        <ResultsGroup>
          <StyledTestResultWrapper>
            <Subtitle>{i18n.sequencedInterview.networkTest.audio}</Subtitle>
            <Flex>
              <Body mb="s4" pr="s8">
                {i18n.sequencedInterview.networkTest.supported}:
              </Body>
              {this.supportedText(results.audio)}
            </Flex>
            <Flex>
              <Body mb="s4" pr="s8">
                {i18n.sequencedInterview.networkTest.quality}:
              </Body>
              {this.mosScoreRating('audio', results.audio.mos)}
            </Flex>
          </StyledTestResultWrapper>
          <StyledTestResultWrapper>
            <Subtitle>{i18n.sequencedInterview.networkTest.video}</Subtitle>
            <Flex>
              <Body mb="s4" pr="s8">
                {i18n.sequencedInterview.networkTest.supported}:
              </Body>
              {this.supportedText(results.video)}
            </Flex>
            <Flex>
              <Body mb="s4" pr="s8">
                {i18n.sequencedInterview.networkTest.quality}:
              </Body>
              {this.mosScoreRating('video', results.video.mos)}
            </Flex>
          </StyledTestResultWrapper>
        </ResultsGroup>
        {(results.video.mos <= 2.0 || results.audio.mos <= 2.0) && (
          // TODO this text is found only in Finnish texts, need to check if it's needed and translate it
          <Body color="error110" mt="s20" fontWeight={600}>
            {i18n.sequencedInterview.errors.qualityErrorText}
          </Body>
        )}
      </>
    )
  }

  render() {
    const { networkTest, qualityTest, skipQualityTest } = this.props
    const renderResults =
      networkTest.testing ||
      networkTest.results ||
      qualityTest.testing ||
      qualityTest.results
    return (
      <Flex alignItems="center" flexDirection="column" mt="s32">
        {renderResults && (
          <ResultsGroup>
            {this.renderNetWorkTestResults()}
            {!skipQualityTest && this.renderQualityTestResults()}
          </ResultsGroup>
        )}
        {qualityTest.results &&
          !networkTest.testing &&
          !skipQualityTest &&
          this.renderQualityStats()}
      </Flex>
    )
  }
}

NetworkTestResults.propTypes = propTypes

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  NetworkTestResults
)
